/** Clicking an acquisition's photo points, drawn from its clustered geojson source. */
import { useEffect, useState } from 'react';
import type { Map as MapLibreMap, MapLayerMouseEvent } from 'maplibre-gl';
import { layerId } from './layers';
import type { LinkSource } from './linkSource';
import type { PhotoProperties } from './photoSource';
import { PhotoViewer } from './PhotoViewer';

export interface PhotoLayerProps {
  map: MapLibreMap;
  /** The acquisition's object id, which keys its source and layers. */
  object: string;
  linkSource: LinkSource;
}

/**
 * Oldest first, so prev/next walks the flight in order. `queryRenderedFeatures` over a
 * tiled source can return the same point twice at tile buffers, so photos are deduped by
 * path first, keeping the first occurrence.
 */
export function orderedPhotos(features: PhotoProperties[]): PhotoProperties[] {
  const seen = new Set<string>();
  const unique = features.filter((f) => {
    if (seen.has(f.path)) return false;
    seen.add(f.path);
    return true;
  });
  return unique.sort((a, b) => String(a.time ?? '').localeCompare(String(b.time ?? '')));
}

export function PhotoLayer({ map, object, linkSource }: PhotoLayerProps) {
  const [photos, setPhotos] = useState<PhotoProperties[] | null>(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const pointsLayer = layerId(object, 'photos');
    const clusterLayer = layerId(object, 'clusters');

    const openPhotos = (e: MapLayerMouseEvent) => {
      // Everything in the clicked tile's point layer, so prev/next has somewhere to go.
      const all = map.queryRenderedFeatures(undefined, { layers: [pointsLayer] });
      const list = orderedPhotos(all.map((f) => f.properties as unknown as PhotoProperties).filter((p) => !!p?.path));
      const clicked = (e.features?.[0]?.properties as unknown as PhotoProperties | undefined)?.path;
      if (!list.length) return;
      setPhotos(list);
      setIndex(Math.max(0, list.findIndex((p) => p.path === clicked)));
    };

    const zoomIn = (e: MapLayerMouseEvent) => {
      map.easeTo({ center: e.lngLat, zoom: Math.min(map.getZoom() + 2, 18) });
    };

    map.on('click', pointsLayer, openPhotos);
    map.on('click', clusterLayer, zoomIn);
    return () => {
      map.off('click', pointsLayer, openPhotos);
      map.off('click', clusterLayer, zoomIn);
    };
  }, [map, object]);

  if (!photos) return null;
  return (
    <PhotoViewer
      photos={photos}
      index={index}
      onIndex={setIndex}
      imageUrlFor={(path) => linkSource.url(object, path)}
      onClose={() => setPhotos(null)}
    />
  );
}
